import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const ease = [0.22, 1, 0.36, 1] as const;

const venueData: Record<string, { name: string; tagline: string; description: string }> = {
  "hi-ibiza": {
    name: "Hï Ibiza",
    tagline: "The world's number one club",
    description:
      "Hï Ibiza consistently ranks among the world's best nightclubs, offering an unparalleled clubbing experience with state-of-the-art production, world-class DJ lineups, and an electric atmosphere that defines modern Ibiza nightlife.",
  },
  ushuaia: {
    name: "Ushuaïa",
    tagline: "Open-air daylight performances",
    description:
      "Ushuaïa Ibiza Beach Hotel revolutionised the island's nightlife scene with its iconic open-air stage, hosting daytime performances by the world's biggest electronic music artists against the backdrop of the Mediterranean sky.",
  },
  pacha: {
    name: "Pacha",
    tagline: "The icon of Ibiza nightlife",
    description:
      "Since 1973, Pacha has been synonymous with Ibiza itself. With its iconic cherry logo and legendary nights, Pacha remains the heartbeat of the island's nightlife culture — a timeless venue that continues to set the standard.",
  },
  amnesia: {
    name: "Amnesia",
    tagline: "Underground energy since 1976",
    description:
      "Amnesia is where Ibiza's underground spirit lives. Known for its iconic terrace and main room, the venue has been at the forefront of electronic music culture for nearly five decades, delivering raw, immersive experiences.",
  },
  dc10: {
    name: "DC-10",
    tagline: "Raw, unfiltered club culture",
    description:
      "DC-10 embodies the raw, unpolished side of Ibiza's club scene. Located near the airport runway, this stripped-back venue attracts purists seeking authentic underground electronic music in its most unfiltered form.",
  },
  lio: {
    name: "Lío",
    tagline: "Cabaret meets nightlife",
    description:
      "Lío blends world-class dining with cabaret performance and late-night energy. Situated in the heart of Ibiza's Marina, it offers a refined yet vibrant evening experience that transitions seamlessly from dinner to the dance floor.",
  },
  "club-chinois": {
    name: "Club Chinois",
    tagline: "Asia-inspired refined nightlife",
    description:
      "Club Chinois brings an Asia-inspired aesthetic to Ibiza's nightlife landscape. With its distinctive décor, curated music programming, and sophisticated atmosphere, it offers a refined alternative to the island's high-energy clubs.",
  },
};

const IbizaNightsVenue = () => {
  const { slug } = useParams<{ slug: string }>();
  const venue = slug ? venueData[slug] : null;

  if (!venue) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navigation />
        <div className="section-spacing container-luxury text-center pt-40">
          <h1 className="font-display text-3xl text-foreground mb-4">Venue Not Found</h1>
          <Link to="/ibiza/nights" className="font-label text-bronze hover:underline">
            ← Back to Arzani Nights
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      {/* Hero */}
      <section className="relative h-[70vh] flex items-end overflow-hidden bg-warm-black">
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/60 to-foreground/30" />
        <div className="relative z-10 container-luxury pb-20 md:pb-28">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1.2, ease }}
          >
            <Link
              to="/ibiza/nights"
              className="font-label text-primary-foreground/40 text-[10px] tracking-[0.2em] uppercase hover:text-primary-foreground/70 transition-colors mb-6 inline-block"
            >
              ← Arzani Nights
            </Link>
          </motion.div>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wider uppercase mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 1.4, ease }}
          >
            {venue.name}
          </motion.h1>
          <motion.p
            className="font-accent text-primary-foreground/50 text-sm md:text-base italic"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1.2, ease }}
          >
            {venue.tagline}
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease }}
          >
            <div className="divider-subtle mb-10" />
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] mb-10">
              {venue.description}
            </p>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-12">
              Through Arzani Nights, we arrange guest list access, VIP table reservations, and
              private transport to and from {venue.name} — ensuring a seamless and elevated
              experience from start to finish.
            </p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-all duration-500"
            >
              Arrange Your Night
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IbizaNightsVenue;
